import { type GetListParams } from "react-admin";

import { createApiClient } from "@/api";
import { type DataProviderSchema } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

import { type BlacklistDeviceDto, type BlacklistDevicesFilter } from "./types";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/vpn/blacklist_devices";

export const fetchBlacklistDevicesListData = async (params: GetListParams) => {
  const filter = params.filter as BlacklistDevicesFilter | undefined;
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: true,
        page: params.pagination?.page,
        limit: filter?.limit,
      },
    },
  });

  const data = response.data?.["hydra:member"] ?? [];
  const total = response.data?.["hydra:totalItems"] ?? 0;

  return {
    data,
    total,
  };
};

export const blacklistDevicesSchema: DataProviderSchema<
  "BlacklistDevice",
  "jsonld"
> = {
  list: getLocalFilteredSortedList(fetchBlacklistDevicesListData),
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });

    if (!data) throw new Error("Blacklist device not found");

    return { data };
  },
  edit: async ({ id, data }) => {
    const { deviceId, createdAt, updatedAt } = data as BlacklistDeviceDto;
    const response = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        deviceId,
        createdAt,
        updatedAt,
      },
    });

    if (!response.data) throw Error("Something went wrong");

    return { data: response.data };
  },
  create: async ({ data: { deviceId } }) => {
    if (!deviceId) throw Error("Device ID is required");
    const { data } = await apiClient.POST(BASE_URL, {
      body: {
        deviceId,
      },
    });

    if (!data) throw Error("Something went wrong");

    return {
      data,
    };
  },
  delete: async ({ id }) => {
    const { data } = await apiClient.DELETE(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });
    return {
      data,
    };
  },
};
